<template>
  <div class="booking-view-container">
    <div class="backdrop-gradient"></div>
    
    <!-- Main Card Container -->
    <v-card 
      class="booking-main-card" 
      elevation="0"
      @touchstart="handleTouchStart" 
      @touchend="handleTouchEnd"
    >
      <!-- Header Section -->
      <div class="booking-header">
        <div class="header-content">
          <div class="logo-section">
            <div class="logo-wrapper">
              <img :src="logo" class="header-logo" alt="Logo" />
            </div>
            <h1 class="header-title">{{ $t('booking.booking') }}</h1>
          </div>
          
          <v-btn 
            class="refresh-btn" 
            icon 
            size="large"
            @click="refreshPage"
          >
            <v-icon class="refresh-icon">mdi-reload</v-icon>
          </v-btn>
        </div>

        <!-- Tab Navigation -->
        <div class="tabs-container">
          <div class="custom-tabs">
            <button
              v-for="(tab, index) in tabsConfig"
              :key="index"
              :class="[
                'custom-tab',
                { 'active': tabs === index }
              ]"
              @click="tabs = index"
            >
              <v-icon class="tab-icon">{{ tab.icon }}</v-icon>
              <span class="tab-text">{{ $t(tab.text) }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Content Area -->
      <div class="content-wrapper">
        <div class="content-container">
          <transition 
            name="slide" 
            mode="out-in"
            @enter="onEnter"
            @leave="onLeave"
          >
            <div v-if="tabs === 0" key="book-now" class="tab-content">
              <BookingPoisList />
            </div>
            <div v-else-if="tabs === 1" key="bookings" class="tab-content">
              <UserBookings />
            </div>
          </transition>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import { ref, defineComponent, watch, onMounted, computed } from 'vue'
import UserBookings from '@/components/general/UserBookings.vue'
import BookingPoisList from '@/components/general/BookingPoisList.vue'
import { getToolbarLogoImage, getBottomBarColor } from '@/assets/Branding/branding.js'
import { useRouter } from 'vue-router'

export default defineComponent({
  name: 'BookingView',
  components: {
    BookingPoisList,
    UserBookings,
  },
  setup() {
    const tabs = ref(0)
    const router = useRouter()
    const logo = ref('')
    const tabColor = ref('')
    let startX = 0

    const tabsConfig = computed(() => [
      {
        icon: 'mdi-format-list-bulleted',
        text: 'booking.book-now-tab'
      },
      {
        icon: 'mdi-calendar-check', 
        text: 'booking.bookings'
      }
    ])

    const handleTouchStart = e => {
      startX = e.touches[0].clientX
    }

    const handleTouchEnd = e => {
      const endX = e.changedTouches[0].clientX
      const diffX = startX - endX

      if (Math.abs(diffX) > 50) {
        if (diffX > 0) {
          tabs.value = Math.min(tabs.value + 1, 1)
        } else {
          tabs.value = Math.max(tabs.value - 1, 0)
        }
      }
    }

    const onEnter = (el) => {
      el.style.opacity = '0'
      el.style.transform = 'translateY(20px)'
      
      setTimeout(() => {
        el.style.transition = 'all 0.4s cubic-bezier(0.23, 1, 0.320, 1)'
        el.style.opacity = '1'
        el.style.transform = 'translateY(0)'
      }, 10)
    }

    const onLeave = (el) => {
      el.style.transition = 'all 0.3s cubic-bezier(0.755, 0.05, 0.855, 0.06)'
      el.style.opacity = '0'
      el.style.transform = 'translateY(-20px)'
    }

    watch(tabs, newValue => {
      if (newValue === 0) {
        router.push({ name: 'BookingPoisList' })
      } else {
        router.push({ name: 'UserBookings' })
      }
    })

    const initializeComponent = () => {
      if (router.currentRoute.value.name === 'BookingPoisList') {
        tabs.value = 0
      } else if (router.currentRoute.value.name === 'UserBookings') {
        tabs.value = 1
      }
    }

    const refreshPage = () => {
      router.go(0)
    }

    onMounted(async () => {
      tabColor.value = await getBottomBarColor()
      logo.value = await getToolbarLogoImage()
      initializeComponent()
    })

    return {
      tabs,
      logo,
      tabColor,
      tabsConfig,
      handleTouchStart,
      handleTouchEnd,
      refreshPage,
      onEnter,
      onLeave,
    }
  },
})
</script>

<style scoped>
.booking-view-container {
  min-height: var(--app-viewport-height, 100vh);
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #F8FAFC 0%, #EDF3FD 100%);
}

.backdrop-gradient {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    linear-gradient(135deg, #F8FAFC 0%, #EDF3FD 100%),
    radial-gradient(circle at 20% 50%, rgba(40, 80, 150, 0.03) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(35, 165, 230, 0.04) 0%, transparent 50%);
  z-index: -1;
}

.booking-main-card {
  margin: 0 auto;
  max-width: 100%;
  min-height: var(--app-viewport-height, 100vh);
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border-radius: 0;
  box-shadow: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.booking-header {
  background: linear-gradient(135deg, #285096 0%, #23A5E6 50%, #59C4FF 100%);
  padding: 0;
  position: relative;
  overflow: hidden;
}

.booking-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 1;
  pointer-events: none;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  position: relative;
  z-index: 2;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 16px;
}

.logo-wrapper {
  width: 52px;
  height: 52px;
  background: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.logo-wrapper:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
}

.header-logo {
  height: 32px;
  width: auto;
  object-fit: contain;
}

.header-title {
  color: white;
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  letter-spacing: -0.3px;
}

.refresh-btn {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.refresh-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.refresh-icon {
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.refresh-btn:hover .refresh-icon {
  transform: rotate(180deg);
}

.tabs-container {
  padding: 0 24px 16px;
  position: relative;
  z-index: 2;
}

.custom-tabs {
  display: flex;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(15px);
  border-radius: 16px;
  padding: 6px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  gap: 6px;
}

.custom-tab {
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 14px 12px;
  background: transparent;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: rgba(255, 255, 255, 0.8);
  min-height: 64px;
}

.custom-tab:hover {
  background: rgba(255, 255, 255, 0.15);
  color: white;
}

.custom-tab.active {
  background: white;
  color: #285096;
  box-shadow: 
    0 4px 12px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
}

.tab-icon {
  font-size: 22px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.custom-tab.active .tab-icon {
  transform: scale(1.1);
  color: #285096;
}

.tab-text {
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  line-height: 1.2;
}

.content-wrapper {
  background: linear-gradient(to bottom, 
    rgba(248, 250, 252, 1) 0%, 
    rgba(237, 243, 253, 1) 100%
  );
  min-height: calc(var(--app-viewport-height, 100vh) - 180px);
}

.content-container {
  position: relative;
  overflow: hidden;
}

.tab-content {
  width: 100%;
  min-height: 500px;
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.4s cubic-bezier(0.23, 1, 0.320, 1);
}

.slide-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.slide-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

@media (max-width: 1200px) {
  .booking-main-card {
    margin: 0;
    border-radius: 0;
  }
}

@media (max-width: 968px) {
  .header-content {
    padding: 14px 20px;
  }
  
  .header-title {
    font-size: 22px;
  }
  
  .tabs-container {
    padding: 0 20px 14px;
  }
  
  .logo-wrapper {
    width: 48px;
    height: 48px;
    padding: 9px;
  }
  
  .header-logo {
    height: 30px;
  }
}

@media (max-width: 768px) {
  .header-content {
    padding: 12px 16px;
  }
  
  .header-title {
    font-size: 20px;
  }
  
  .logo-wrapper {
    width: 44px;
    height: 44px;
    padding: 8px;
  }
  
  .header-logo {
    height: 28px;
  }
  
  .tabs-container {
    padding: 0 16px 12px;
  }
  
  .custom-tab {
    padding: 12px 10px;
    min-height: 56px;
  }
  
  .tab-icon {
    font-size: 20px;
  }
  
  .tab-text {
    font-size: 12px;
  }
}

@media (max-width: 450px) {
  .header-content {
    padding: 10px 12px;
    gap: 10px;
  }
  
  .header-title {
    font-size: 18px;
  }
  
  .logo-wrapper {
    width: 40px;
    height: 40px;
    padding: 7px;
  }
  
  .header-logo {
    height: 26px;
  }
  
  .tabs-container {
    padding: 0 12px 10px;
  }
  
  .custom-tab {
    padding: 10px 8px;
    min-height: 50px;
  }
  
  .tab-icon {
    font-size: 18px;
  }
  
  .tab-text {
    font-size: 11px;
  }
}

@media (max-width: 355px) {
  .header-content {
    padding: 8px 10px;
  }
  
  .header-title {
    font-size: 16px;
  }
  
  .logo-wrapper {
    width: 36px;
    height: 36px;
    padding: 6px;
  }
  
  .header-logo {
    height: 24px;
  }
  
  .tabs-container {
    padding: 0 10px 8px;
  }
  
  .custom-tab {
    padding: 8px 6px;
    min-height: 45px;
    gap: 4px;
  }
  
  .tab-icon {
    font-size: 16px;
  }
  
  .tab-text {
    font-size: 10px;
  }
}
</style>
